import { z } from 'zod';
/**
 * Контракты эндпоинтов `modules/dictionaries` (docs/api-design.md §4.3,
 * docs/data-model.md §3.3, docs/REQUIREMENTS.md FR-2).
 *
 * Справочники — системный ресурс (не per-space). Любой reference-поле
 * любого пространства ссылается на справочник напрямую через dictionaryId.
 *
 * Инварианты, проверяемые на уровне схем:
 *  - slug колонки: строчные латиница+цифры+underscore, 1..50 символов
 *  - columnType из фиксированного enum
 *  - values в DictionaryEntry — объект с произвольными ключами (slug → value)
 */
// ─── Column types ─────────────────────────────────────────────────────────
export const DICT_COLUMN_TYPES = ['text', 'number', 'boolean', 'select', 'multi_select', 'url', 'reference'];
export const DictColumnTypeSchema = z.enum(DICT_COLUMN_TYPES);
// slug колонки — ключ в values JSONB: строчные, цифры, underscore
const COLUMN_SLUG_RE = /^[a-z][a-z0-9_]{0,49}$/;
// ─── Ссылка на пользователя (владелец / редактор) ──────────────────────────
// Минимальный публичный slice пользователя для секции «Права и доступ».
export const DictionaryUserRefSchema = z.object({
    userId: z.string().uuid(),
    login: z.string(),
    displayName: z.string(),
});
// ─── DictionaryColumn DTO ─────────────────────────────────────────────────
export const DictionaryColumnSchema = z.object({
    id: z.string().uuid(),
    dictionaryId: z.string().uuid(),
    name: z.string().min(1).max(120),
    slug: z.string().regex(COLUMN_SLUG_RE),
    columnType: DictColumnTypeSchema,
    config: z.record(z.unknown()),
    sortOrder: z.number().int(),
});
// ─── Dictionary DTO ───────────────────────────────────────────────────────
export const DictionarySchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    createdById: z.string().uuid(),
    ownerId: z.string().uuid(),
    owner: DictionaryUserRefSchema,
    keyColumnId: z.string().uuid().nullable(),
    displayColumnId: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    columns: z.array(DictionaryColumnSchema),
    // Может ли текущий пользователь редактировать структуру/записи справочника
    // (platform_admin ИЛИ владелец ИЛИ редактор). Вычисляется бэкендом под
    // текущего пользователя — фронт использует для сокрытия мутирующих контролов.
    canEdit: z.boolean(),
    // Может ли текущий пользователь видеть справочник во вкладке «Справочники»
    // (platform_admin ИЛИ создатель ИЛИ владелец ИЛИ редактор ИЛИ наблюдатель).
    // Ограничение касается только вкладки; reference-поля в карточках и
    // представлениях от него не зависят. Фронт гейтит страницу детали по флагу.
    canView: z.boolean(),
});
// Краткая версия для списка (без columns и entries)
export const DictionaryListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    createdById: z.string().uuid(),
    ownerId: z.string().uuid(),
    owner: DictionaryUserRefSchema,
    keyColumnId: z.string().uuid().nullable(),
    displayColumnId: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    columnCount: z.number().int(),
    entryCount: z.number().int(),
});
// ─── DictionaryEntry DTO ──────────────────────────────────────────────────
export const DictionaryEntrySchema = z.object({
    id: z.string().uuid(),
    dictionaryId: z.string().uuid(),
    values: z.record(z.unknown()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DictionaryEntryListResponseSchema = z.object({
    items: z.array(DictionaryEntrySchema),
    nextCursor: z.string().nullable(),
    total: z.number().int().nullable(),
});
// ─── DictionaryEditor DTO ────────────────────────────────────────────────
// Тот же slice, что и владелец, — переиспользуем DictionaryUserRefSchema.
export const DictionaryEditorSchema = DictionaryUserRefSchema;
// ─── POST /api/dictionaries ───────────────────────────────────────────────
export const CreateDictionaryRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).optional(),
});
// ─── PATCH /api/dictionaries/:id ─────────────────────────────────────────
export const UpdateDictionaryRequestSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(1000).nullable().optional(),
    keyColumnId: z.string().uuid().nullable().optional(),
    displayColumnId: z.string().uuid().nullable().optional(),
});
// ─── POST /api/dictionaries/:id/columns ──────────────────────────────────
export const CreateDictionaryColumnRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    slug: z.string().trim().regex(COLUMN_SLUG_RE),
    columnType: DictColumnTypeSchema,
    config: z.record(z.unknown()).optional().default({}),
    sortOrder: z.number().int().optional(),
});
// ─── PATCH /api/dictionaries/:id/columns/:colId ───────────────────────────
export const UpdateDictionaryColumnRequestSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    config: z.record(z.unknown()).optional(),
    sortOrder: z.number().int().optional(),
});
// ─── POST/PUT/PATCH /api/dictionaries/:id/entries ────────────────────────
export const UpsertDictionaryEntryRequestSchema = z.object({
    values: z.record(z.unknown()),
});
// ─── GET /api/dictionaries/:id/entries — query params ────────────────────
export const ListEntriesQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(500).optional().default(100),
    search: z.string().trim().optional(),
});
// ─── POST /api/dictionaries/:id/editors ──────────────────────────────────
export const AddEditorRequestSchema = z.object({
    userId: z.string().uuid(),
});
// ─── POST /api/dictionaries/:id/viewers ──────────────────────────────────
// Наблюдатель — доступ «только просмотр» во вкладке «Справочники».
export const AddViewerRequestSchema = z.object({
    userId: z.string().uuid(),
});
// DTO наблюдателя — тот же публичный slice пользователя, что и у редактора.
export const DictionaryViewerSchema = DictionaryUserRefSchema;
// ─── POST /api/dictionaries/:id/owner — передача владения ─────────────────
export const TransferOwnerRequestSchema = z.object({
    userId: z.string().uuid(),
});
